import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Wifi, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NetworkRecovery } from "@/components/NetworkRecovery";
import { NotificationSettings } from "@/components/NotificationSettings";

const COLORS = ["red", "sky", "amber", "emerald", "violet", "slate"];

export default function AdminSettings() {
  const { pharmacy, setPharmacy, net, user } = useAuth();
  const [form, setForm] = useState(null);
  const [ip, setIp] = useState(net.ip);
  const [newIp, setNewIp] = useState("");
  const [newType, setNewType] = useState({ key: "", label: "", color: "sky" });
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (pharmacy) setForm({ ...pharmacy });
  }, [pharmacy]);

  useEffect(() => {
    api.get("/net/ip").then(({ data }) => setIp(data.ip)).catch(() => {});
  }, []);

  const persist = useCallback(
    async (updates) => {
      try {
        const { data } = await api.put("/pharmacy", updates);
        setPharmacy(data);
        setForm(data);
        toast.success("Impostazioni salvate");
      } catch (e) {
        toast.error(apiError(e));
      }
    },
    [setPharmacy]
  );

  const savePassword = useCallback(async () => {
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    try {
      await api.put(`/operators/${user.id}`, { password });
      setPassword("");
      toast.success("Password aggiornata");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [password, user.id]);

  if (!form) return null;

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Impostazioni</h1>
        <p className="text-sm text-gray-600 mt-1">
          Codice farmacia per il login operatori: <span className="font-mono-num font-semibold">{form.code}</span>
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold">Dati farmacia</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Nome</label>
            <input
              data-testid="settings-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Indirizzo</label>
            <input
              data-testid="settings-address"
              value={form.address || ""}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Ore settimanali previste</label>
            <input
              data-testid="settings-weekly-hours"
              type="number"
              value={form.weekly_hours}
              onChange={(e) => setForm({ ...form, weekly_hours: Number(e.target.value) })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num"
            />
          </div>
        </div>
        <button
          data-testid="settings-save"
          onClick={() => persist({ name: form.name, address: form.address, weekly_hours: form.weekly_hours })}
          className="h-11 px-6 rounded-lg bg-emerald-600 text-white font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95"
        >
          <Save className="h-4 w-4" /> Salva
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold">Accesso solo dal WiFi della farmacia</h2>
        <p className="text-sm text-gray-600">
          Se la lista è vuota l'accesso è consentito da qualsiasi rete. Aggiungi l'IP pubblico della farmacia per limitare le timbrature.
        </p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Wifi className="h-4 w-4 text-emerald-600" />
          IP attuale: <span data-testid="settings-current-ip" className="font-mono-num font-semibold">{ip}</span>
          <button
            data-testid="add-current-ip"
            onClick={() => persist({ allowed_ips: [...new Set([...(form.allowed_ips || []), ip])] })}
            className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-700 text-xs font-semibold transition-colors hover:bg-emerald-50"
          >
            Consenti questa rete
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="allowed-ips">
          {(form.allowed_ips || []).map((v) => (
            <span key={v} className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-mono-num">
              {v}
              <button
                data-testid={`remove-ip-${v}`}
                onClick={() => persist({ allowed_ips: form.allowed_ips.filter((x) => x !== v) })}
                aria-label={`Rimuovi ${v}`}
                className="text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {(form.allowed_ips || []).length === 0 && <span className="text-sm text-gray-600">Nessun IP configurato.</span>}
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            data-testid="new-ip-input"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            placeholder="es. 93.44.12.8"
            className="h-11 w-56 rounded-lg border border-gray-200 px-3 text-sm font-mono-num"
          />
          <button
            data-testid="add-ip-btn"
            onClick={() => { if (newIp.trim()) { persist({ allowed_ips: [...new Set([...(form.allowed_ips || []), newIp.trim()])] }); setNewIp(""); } }}
            className="h-11 px-5 rounded-lg border border-gray-200 text-sm font-semibold flex items-center gap-2 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" /> Aggiungi IP
          </button>
        </div>
        <label className="flex items-center gap-3 text-sm">
          <input
            data-testid="enforce-owner-toggle"
            type="checkbox"
            checked={!!form.enforce_ip_for_owner}
            onChange={(e) => {
              const value = e.target.checked;
              setForm({ ...form, enforce_ip_for_owner: value });
              persist({ enforce_ip_for_owner: value });
            }}
            className="h-4 w-4 accent-emerald-600"
          />
          Applica la restrizione anche al titolare
        </label>
      </div>

      <NetworkRecovery />
      <NotificationSettings />

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold">Tipi di assenza</h2>
        <div className="flex flex-wrap gap-2" data-testid="absence-types-list">
          {form.absence_types.map((t) => (
            <span key={t.key} className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">
              {t.label}
              <button
                data-testid={`remove-type-${t.key}`}
                onClick={() => persist({ absence_types: form.absence_types.filter((x) => x.key !== t.key) })}
                aria-label={`Rimuovi ${t.label}`}
                className="text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Nome tipo</label>
            <input
              data-testid="new-type-label"
              value={newType.label}
              onChange={(e) => setNewType({ ...newType, label: e.target.value })}
              placeholder="Congedo parentale"
              className="mt-1 h-11 w-56 rounded-lg border border-gray-200 px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">Colore</label>
            <select
              data-testid="new-type-color"
              value={newType.color}
              onChange={(e) => setNewType({ ...newType, color: e.target.value })}
              className="mt-1 h-11 rounded-lg border border-gray-200 px-3 text-sm"
            >
              {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button
            data-testid="add-type-btn"
            onClick={() => {
              const label = newType.label.trim();
              if (!label) return;
              const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              persist({ absence_types: [...form.absence_types, { key, label, color: newType.color }] });
              setNewType({ key: "", label: "", color: "sky" });
            }}
            className="h-11 px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 transition-colors hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" /> Aggiungi tipo
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
        <h2 className="text-base font-semibold">Password titolare</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <input
            data-testid="new-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="nuova password"
            className="h-11 w-64 max-w-full rounded-lg border border-gray-200 px-3 text-sm"
          />
          <button
            data-testid="save-password-btn"
            onClick={savePassword}
            className="h-11 px-5 rounded-lg bg-emerald-600 text-white text-sm font-semibold transition-colors hover:bg-emerald-700"
          >
            Aggiorna password
          </button>
        </div>
      </div>
    </div>
  );
}
